import React, { useState, useContext, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { uploadDocuments } from '../../../../../services/api';
import ThemeContext from '../../../../../ThemeContext';

const AddDocumentModal = ({ show, onHide, folderId, onSuccess }) => {
  const { theme } = useContext(ThemeContext);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, failed: 0, total: 0 });
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = [];
    const oversizedFiles = [];

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizedFiles.length > 0) {
      toast.warning(
        `These files exceed 1MB limit and were removed: ${oversizedFiles.join(', ')}`,
        {
          style: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.warning,
          },
        }
      );
    }

    setSelectedFiles(validFiles);
    setUploadProgress({ uploaded: 0, failed: oversizedFiles.length, total: files.length });
  };

  const handleAddFiles = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Please select files to upload', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
        },
      });
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      
      selectedFiles.forEach((file) => {
        formData.append('files[]', file);
      });

      console.log('Uploading files to folder:', folderId);
      console.log('Files count:', selectedFiles.length);

      await uploadDocuments(folderId, formData);

      toast.success('Documents uploaded successfully', {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
        },
      });
      setSelectedFiles([]);
      setUploadProgress({ uploaded: selectedFiles.length, failed: 0, total: selectedFiles.length });
      onSuccess();
      onHide();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      const validationErrors = error.response?.data?.errors;
      
      let displayMessage = `Failed to upload documents: ${errorMessage}`;
      if (validationErrors) {
        displayMessage += '\n' + Object.values(validationErrors).flat().join('\n');
      }
      
      toast.error(displayMessage, {
        style: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
        },
      });
      setUploadProgress({ uploaded: 0, failed: selectedFiles.length, total: selectedFiles.length });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setUploadProgress({ uploaded: 0, failed: 0, total: 0 });
      onHide();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header
        style={{
          backgroundColor: theme.colors.primary,
          color: theme.colors.textWhite,
          borderBottom: 'none',
        }}
      >
        <Modal.Title style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
          Upload Files to Folder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
        }}
      >
        <div style={{ display: 'flex', gap: '1rem' }}>
          {/* Files List */}
          <div style={{ flex: 1 }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.tiff,.mp3,.wav,.aac,.wma,.mpeg,.mpg,.mpeg-1,.mpeg-2,.wmv,.avi,.mov"
            />
            
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(9px, 2vw, 10px)' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.colors.borderColor}` }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 'bold' }}>Size</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedFiles.length === 0 ? (
                  <tr>
                    <td colSpan="3" style={{ padding: '2rem', textAlign: 'center', color: theme.colors.textSecondary }}>
                      No files selected
                    </td>
                  </tr>
                ) : (
                  selectedFiles.map((file, index) => (
                    <tr key={index} style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}>
                      <td style={{ padding: '0.5rem' }}>{file.name}</td>
                      <td style={{ padding: '0.5rem' }}>{formatFileSize(file.size)}</td>
                      <td style={{ padding: '0.5rem' }}>
                        {isUploading ? (
                          <span style={{ color: theme.colors.warning }}>Uploading...</span>
                        ) : (
                          <span style={{ color: theme.colors.textSecondary }}>Ready</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div style={{ marginTop: '1rem', fontSize: 'clamp(8px, 2vw, 9px)', color: theme.colors.textSecondary }}>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Uploaded:</strong> {uploadProgress.uploaded} | <strong>Failed:</strong> {uploadProgress.failed} | <strong>Total:</strong> {uploadProgress.total}
              </p>
            </div>
          </div>

          {/* Supported Formats */}
          <div
            style={{
              width: '200px',
              padding: '1rem',
              backgroundColor: theme.colors.bgSecondary,
              borderRadius: '5px',
              fontSize: 'clamp(8px, 2vw, 9px)',
            }}
          >
            <h6 style={{ marginBottom: '0.5rem', fontSize: 'clamp(9px, 2vw, 10px)', fontWeight: 'bold' }}>
              Supported formats
            </h6>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Document:</strong><br />
              PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Image:</strong><br />
              PNG, JPEG, GIF, TIFF
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Audio:</strong><br />
              MP3, WAV, AAC, WMA
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Video:</strong><br />
              MPEG-4, MPEG-1, MPEG-2, WMV, AVI, MOV
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Max File Size: 1MB</strong>
            </p>
            <p style={{ margin: '0.5rem 0', fontStyle: 'italic', color: theme.colors.textSecondary }}>
              The system may convert the files to other formats for storage and display purposes
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        style={{
          backgroundColor: theme.colors.bgPrimary,
          borderTop: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <button
          onClick={handleAddFiles}
          disabled={isUploading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            cursor: isUploading ? 'not-allowed' : 'pointer',
            opacity: isUploading ? 0.6 : 1,
          }}
        >
          Add Files
        </button>
        <button
          onClick={handleUpload}
          disabled={isUploading || selectedFiles.length === 0}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: theme.colors.success,
            color: theme.colors.textWhite,
            cursor: isUploading || selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
            opacity: isUploading || selectedFiles.length === 0 ? 0.6 : 1,
          }}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
        <button
          onClick={handleClose}
          disabled={isUploading}
          style={{
            padding: 'clamp(6px, 2vw, 8px) clamp(12px, 3vw, 16px)',
            fontSize: 'clamp(9px, 2vw, 10px)',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: isUploading ? theme.colors.bgSecondary : theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            cursor: isUploading ? 'not-allowed' : 'pointer',
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddDocumentModal;